import { useState, useEffect, useMemo } from "react";
import { Search, Zap, ShieldCheck, Package, Coins, Wallet, TrendingUp, Heart, ArrowDownAZ, CircleDollarSign, Star, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AppRow from "@/components/AppRow";
import ActivationWizard from "@/components/ActivationWizard";
import PixCheckout from "@/components/PixCheckout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const FAV_KEY = "vulpix_favs";
const loadFavs = () => { try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); } catch { return new Set(); } };

const SORTS = [
  { id: "az", label: "A-Z", icon: ArrowDownAZ },
  { id: "hot", label: "Em alta", icon: TrendingUp },
  { id: "price", label: "Preço", icon: CircleDollarSign },
  { id: "fav", label: "Favoritos", icon: Heart },
];

const priceStr = (p) => (p != null ? Number(p).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : null);

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [actToday, setActToday] = useState({ count: 0, count_7d: 0 });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("az");
  const [favs, setFavs] = useState(loadFavs);
  const [selected, setSelected] = useState(null);
  const [payMethod, setPayMethod] = useState("credits");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pixOpen, setPixOpen] = useState(false);
  const [mobilePayOpen, setMobilePayOpen] = useState(false);

  useEffect(() => {
    api.get("/apps").then(({ data }) => {
      setApps(data);
      if (data.length) {
        const top = [...data].sort((a, b) => (b.activations_count || 0) - (a.activations_count || 0))[0];
        setSelected(top);
      }
    }).catch(() => setApps([]));
  }, []);

  useEffect(() => {
    if (user?.store_locked && user?.locked_store_slug) navigate(`/loja/${user.locked_store_slug}`, { replace: true });
  }, [user]);

  useEffect(() => { api.get("/apps/activations-today").then(({ data }) => setActToday(data)).catch(() => {}); }, []);

  const hotIds = useMemo(() => {
    if (!apps) return new Set();
    return new Set([...apps].filter((a) => (a.activations_count || 0) > 0)
      .sort((a, b) => (b.activations_count || 0) - (a.activations_count || 0))
      .slice(0, 5).map((a) => a.id));
  }, [apps]);

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(FAV_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const list = useMemo(() => {
    if (!apps) return null;
    const term = q.trim().toLowerCase();
    let arr = apps.filter((a) => !term || a.name?.toLowerCase().includes(term) || a.category?.toLowerCase().includes(term));
    if (sort === "fav") arr = arr.filter((a) => favs.has(a.id));
    const byName = (a, b) => String(a.name).localeCompare(String(b.name), "pt-BR");
    if (sort === "price") arr = [...arr].sort((a, b) => (a.price ?? 1e9) - (b.price ?? 1e9) || byName(a, b));
    else if (sort === "hot") arr = [...arr].sort((a, b) => (b.activations_count || 0) - (a.activations_count || 0) || byName(a, b));
    else arr = [...arr].sort(byName);
    return arr;
  }, [apps, q, sort, favs]);

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />

      {/* Top search band (prominent, red-accented) */}
      <div className="sticky top-20 z-30 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-0 h-40 w-[70%] -translate-x-1/2 rounded-full bg-primary/15 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 h-6 w-6 -translate-y-1/2 text-primary" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Pesquisar e ativar aplicativo..."
              data-testid="app-search-input"
              className="h-16 rounded-xl border-2 border-primary/40 bg-card pl-14 pr-12 text-lg font-medium shadow-[0_0_0_4px_hsl(var(--primary)/0.06)] transition-all placeholder:text-muted-foreground focus-visible:border-primary focus-visible:shadow-[0_0_0_5px_hsl(var(--primary)/0.12)] focus-visible:ring-0 sm:text-xl"
            />
            {q && (
              <button onClick={() => setQ("")} data-testid="search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          {(actToday.count > 0 || actToday.count_7d > 0) && (
            <div className="mt-3 flex justify-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 px-3 py-1 text-xs font-semibold text-green-500" data-testid="landing-activations-today">
                <ShieldCheck className="h-3.5 w-3.5" /> {actToday.count > 0 ? `${actToday.count} ativações hoje` : `${actToday.count_7d} ativações nos últimos 7 dias`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile selected-app bar (sticky, keeps selection visible) */}
      {selected && (
        <div className="sticky top-[152px] z-20 border-b border-border bg-card/95 backdrop-blur lg:hidden" data-testid="mobile-selected-bar">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 sm:px-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
              {selected.logo ? (
                <img src={selected.logo} alt={selected.name} className="h-full w-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; e.target.parentElement.querySelector("[data-fb]").style.display = "flex"; }} />
              ) : null}
              <Package data-fb className="h-5 w-5 text-muted-foreground" style={{ display: selected.logo ? "none" : "flex" }} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-heading text-sm font-bold" data-testid="mobile-selected-name">{selected.name}</div>
              <div className="font-num text-xs text-muted-foreground">
                {priceStr(selected.price) ? `R$ ${priceStr(selected.price)}` : ""} · <span className="text-foreground">{selected.credit_cost}</span> cr.
              </div>
            </div>
            <Button size="sm" className="shrink-0 gap-1.5 glow-red" data-testid="mobile-activate-btn" onClick={() => setMobilePayOpen(true)}>
              <Zap className="h-4 w-4" /> Ativar
            </Button>
          </div>
        </div>
      )}

      <section id="aplicativos" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Left: sort + list */}
          <div className="lg:col-span-8">
            <div className="mb-3 flex items-center gap-1 overflow-x-auto no-scrollbar border-b border-border">
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSort(s.id)}
                  data-testid={`sort-${s.id}`}
                  className={`-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                    sort === s.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <s.icon className="h-4 w-4" /> {s.label}
                </button>
              ))}
              <span className="ml-auto hidden shrink-0 pr-1 text-xs text-muted-foreground sm:block" data-testid="apps-count">
                {list ? `${list.length} apps` : ""}
              </span>
            </div>

            <div className="scroll-thin max-h-[640px] overflow-y-auto rounded-xl border border-border bg-card">
              {list === null && (
                <div className="space-y-1 p-3">
                  {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-md" />)}
                </div>
              )}
              {list && list.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-20 text-center text-muted-foreground" data-testid="apps-empty">
                  <Package className="h-8 w-8" />
                  {sort === "fav" ? "Nenhum favorito ainda. Toque na estrela para salvar." : "Nenhum aplicativo encontrado."}
                </div>
              )}
              {list && list.map((app) => (
                <AppRow
                  key={app.id}
                  app={app}
                  selected={selected?.id === app.id}
                  hot={hotIds.has(app.id)}
                  fav={favs.has(app.id)}
                  onSelect={setSelected}
                  onToggleFav={toggleFav}
                />
              ))}
            </div>
          </div>

          {/* Right: wallet + action panel (desktop) */}
          <div className="hidden lg:col-span-4 lg:block">
            <div className="lg:sticky lg:top-44 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-card p-3" data-testid="wallet-credits">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground"><Coins className="h-3.5 w-3.5 text-primary" /> Créditos</div>
                  <div className="mt-1 font-num text-xl font-bold">{user ? (user.credits ?? 0) : "0"}</div>
                </div>
                <div className="rounded-xl border border-border bg-card p-3" data-testid="wallet-saldo">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground"><Wallet className="h-3.5 w-3.5 text-primary" /> Saldo</div>
                  <div className="mt-1 font-num text-xl font-bold">{user ? `R$ ${Number(user.balance ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "R$ 0,00"}</div>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="action-panel">
                <div className="border-b border-border bg-muted/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-primary">Ação e pagamento</div>
                {!selected ? (
                  <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
                    <Package className="h-7 w-7" /> Selecione um aplicativo na lista.
                  </div>
                ) : (
                  <div className="p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                        {selected.logo ? (
                          <img src={selected.logo} alt={selected.name} className="h-full w-full object-cover"
                            onError={(e) => { e.target.style.display = "none"; e.target.parentElement.querySelector("[data-fb]").style.display = "flex"; }} />
                        ) : null}
                        <Package data-fb className="h-6 w-6 text-muted-foreground" style={{ display: selected.logo ? "none" : "flex" }} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-heading text-lg font-bold" data-testid="panel-app-name">{selected.name}</div>
                        <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted-foreground"><ShieldCheck className="h-3 w-3 text-primary" /> Ativação anual</div>
                      </div>
                      <button onClick={() => toggleFav(selected.id)} data-testid="panel-fav" className={favs.has(selected.id) ? "text-primary" : "text-muted-foreground/50 hover:text-primary"}>
                        <Star className={`h-5 w-5 ${favs.has(selected.id) ? "fill-primary" : ""}`} />
                      </button>
                    </div>

                    <div className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Forma de pagamento</div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <button
                        onClick={() => (user ? setPayMethod("credits") : navigate("/login"))}
                        data-testid="pay-credits"
                        className={`relative rounded-lg border p-3 text-left transition-colors ${payMethod === "credits" ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border hover:bg-muted/60"}`}
                      >
                        {!user && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary/70" />}
                        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"><Coins className="h-3.5 w-3.5" /> Créditos</div>
                        <div className="mt-1 font-num text-lg font-bold">{selected.credit_cost}</div>
                      </button>
                      <button
                        onClick={() => (user ? setPayMethod("balance") : navigate("/login"))}
                        data-testid="pay-balance"
                        className={`relative rounded-lg border p-3 text-left transition-colors ${payMethod === "balance" ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border hover:bg-muted/60"}`}
                      >
                        {!user && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary/70" />}
                        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Saldo</div>
                        <div className="mt-1 font-num text-lg font-bold">{priceStr(selected.price) ? `R$ ${priceStr(selected.price)}` : "—"}</div>
                      </button>
                      <button
                        onClick={() => setPayMethod("pix")}
                        data-testid="pay-pix"
                        className={`relative rounded-lg border p-3 text-left transition-colors ${payMethod === "pix" ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border hover:bg-muted/60"}`}
                      >
                        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Pix</div>
                        <div className="mt-1 font-num text-lg font-bold">{priceStr(selected.price) ? `R$ ${priceStr(selected.price)}` : "—"}</div>
                      </button>
                    </div>

                    <Button className="mt-4 h-12 w-full gap-2 rounded-lg text-base glow-red" data-testid="activate-now-btn" onClick={() => { if (payMethod === "pix") return setPixOpen(true); if (!user) return navigate("/login"); setWizardOpen(true); }}>
                      <Zap className="h-5 w-5" /> Ativar agora
                    </Button>
                    <p className="mt-2 text-center text-[11px] text-muted-foreground">
                      {payMethod === "pix" ? "Pague via Pix e ativamos automaticamente após a confirmação." : payMethod === "balance" ? "O valor em R$ sai do seu saldo — estornamos se a ativação falhar." : "Créditos só são debitados se a ativação for concluída."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile payment selector modal */}
      <Dialog open={mobilePayOpen} onOpenChange={setMobilePayOpen}>
        <DialogContent data-testid="mobile-pay-dialog">
          <DialogHeader><DialogTitle>Ação e pagamento</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                  {selected.logo ? (
                    <img src={selected.logo} alt={selected.name} className="h-full w-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; e.target.parentElement.querySelector("[data-fb]").style.display = "flex"; }} />
                  ) : null}
                  <Package data-fb className="h-6 w-6 text-muted-foreground" style={{ display: selected.logo ? "none" : "flex" }} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-heading text-base font-bold">{selected.name}</div>
                  <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted-foreground"><ShieldCheck className="h-3 w-3 text-primary" /> Ativação anual</div>
                </div>
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Forma de pagamento</div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => (user ? setPayMethod("credits") : navigate("/login"))} data-testid="pay-credits-m"
                  className={`relative rounded-lg border p-3 text-left transition-colors ${payMethod === "credits" ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border"}`}>
                  {!user && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary/70" />}
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase text-muted-foreground"><Coins className="h-3.5 w-3.5" /> Créditos</div>
                  <div className="mt-1 font-num text-lg font-bold">{selected.credit_cost}</div>
                </button>
                <button onClick={() => (user ? setPayMethod("balance") : navigate("/login"))} data-testid="pay-balance-m"
                  className={`relative rounded-lg border p-3 text-left transition-colors ${payMethod === "balance" ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border"}`}>
                  {!user && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary/70" />}
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Saldo</div>
                  <div className="mt-1 font-num text-lg font-bold">{priceStr(selected.price) ? `R$ ${priceStr(selected.price)}` : "—"}</div>
                </button>
                <button onClick={() => setPayMethod("pix")} data-testid="pay-pix-m"
                  className={`relative rounded-lg border p-3 text-left transition-colors ${payMethod === "pix" ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border"}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Pix</div>
                  <div className="mt-1 font-num text-lg font-bold">{priceStr(selected.price) ? `R$ ${priceStr(selected.price)}` : "—"}</div>
                </button>
              </div>
              <Button className="h-12 w-full gap-2 rounded-lg text-base glow-red" data-testid="mobile-pay-activate"
                onClick={() => { setMobilePayOpen(false); if (payMethod === "pix") return setPixOpen(true); if (!user) return navigate("/login"); setWizardOpen(true); }}>
                <Zap className="h-5 w-5" /> Ativar agora
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                {payMethod === "pix" ? "Pague via Pix e ativamos automaticamente após a confirmação." : payMethod === "balance" ? "O valor em R$ sai do seu saldo — estornamos se a ativação falhar." : "Créditos só são debitados se a ativação for concluída."}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
      <ActivationWizard app={selected} open={wizardOpen} onOpenChange={setWizardOpen} />
      <PixCheckout app={selected} open={pixOpen} onOpenChange={setPixOpen} />
    </div>
  );
}
